import React, { MouseEvent } from "react";
import AutocompleteConfig from "src/components/autocomplete/autocomplete_config";
import { AutocompleteSource, getAlgoliaResults } from "@algolia/autocomplete-js";
import { LiteClient } from "algoliasearch/lite";
import { BaseItem } from "@algolia/autocomplete-core";
import { Availability, FulfillmentMethod } from "src/storefront/types/catalog";
import { Temporal } from "@js-temporal/polyfill";
import { ReactComponent as ImageSquareFallback } from "images/image-square-fallback.svg";
import { ReactComponent as SearchIcon } from "images/icons/search-alternate.svg";
import searchInsights from "search-insights";
import { NavigationState, serializeShopState, StorefrontPage } from "src/storefront/types/navigation";
import { TopSearch } from "@algolia/client-analytics";
import { SetContext } from "src/components/autocomplete/autocomplete_config";

export interface ProductVariant extends BaseItem {
  key: string;
  keyPath: string;
  title: string;
  image?: string;
  fulfillmentMethods: FulfillmentMethod[];
  availabilities: { start_time: string; end_time: string; wday: number }[];
}

interface ProcessedProductVariant extends BaseItem {
  key: string;
  keyPath: string;
  title: string;
  image?: string;
  fulfillmentMethods: FulfillmentMethod[];
  availabilities: Availability[];
}

export interface ProductResult extends BaseItem {
  objectID: string;
  fulfillmentMethods: FulfillmentMethod[];
  products: ProductVariant[];
  title: string;
  __autocomplete_queryID?: string;
}

export default class ProductAutocompleteConfig extends AutocompleteConfig<ProductResult> {
  readonly storeId: number;
  readonly storePermalink: string;
  readonly selectedFulfillmentMethod: FulfillmentMethod;
  readonly imageBaseUrl: string;
  readonly onResultClick: (fullEncodedKeyPath: string[], queryId: string | undefined) => void;
  readonly onTopSearchClick: (newQuery: string) => void;
  readonly searchAppId: string;
  readonly searchPublicApiKey: string;
  readonly topSearchFetchResult: TopSearch[];
  readonly topRecommendations: ProductResult[];

  constructor({
    indexName,
    storeId,
    storePermalink,
    selectedFulfillmentMethod,
    imageBaseUrl,
    onResultClick,
    onTopSearchClick,
    searchAppId,
    searchPublicApiKey,
    topSearchFetchResult,
    topRecommendations,
  }: {
    indexName: string;
    storeId: number;
    storePermalink: string;
    selectedFulfillmentMethod: FulfillmentMethod;
    imageBaseUrl: string;
    onResultClick: (fullEncodedKeyPath: string[], queryId: string | undefined) => void;
    onTopSearchClick: (newQuery: string) => void;
    searchAppId: string;
    searchPublicApiKey: string;
    topSearchFetchResult: TopSearch[];
    topRecommendations: ProductResult[];
  }) {
    super(indexName, searchAppId, searchPublicApiKey);

    this.storeId = storeId;
    this.storePermalink = storePermalink;
    this.selectedFulfillmentMethod = selectedFulfillmentMethod;
    this.imageBaseUrl = imageBaseUrl;
    this.onResultClick = onResultClick;
    this.onTopSearchClick = onTopSearchClick;
    this.indexName = indexName;
    this.searchPublicApiKey = searchPublicApiKey;
    this.searchAppId = searchAppId;
    this.topSearchFetchResult = topSearchFetchResult; 
    this.topRecommendations = topRecommendations;
  }

  emptyQueryCollectionId = "product_suggestions"
  emptyQueryCollectionTitle = "product_search_results";
  queryCollectionId = "products";

  // TODO: Product suggestions
  emptyQueryResults(): AutocompleteSource<ProductResult> {
    return {
      sourceId: this.emptyQueryCollectionId,
      getItems: () => [],
      templates: {
        item() {
          return '<div></div>'
        }
      }
    }
  }

  filterString(): string {
    return `storeID:${this.storeId} AND fulfillmentMethods:${this.selectedFulfillmentMethod}`;
  }

  itemComponent(item: ProductResult, index: number): React.JSX.Element {
    const productVariant = this.selectMostRelevantVariant(item);
    const encodedKeyPath = productVariant.keyPath.split(";").map((segment) => encodeURIComponent(segment))
    const imageUrl = productVariant.image ? `${this.imageBaseUrl}/${productVariant.image}` : undefined;

    const onClick = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (item.__autocomplete_queryID) {
        searchInsights("clickedObjectIDsAfterSearch", {
          index: this.indexName,
          eventName: "Product clicked after search",
          queryID: item.__autocomplete_queryID,
          objectIDs: [item.objectID.toString()],
          positions: [index + 1], // Positions start from 1
        })
      }

      this.onResultClick(encodedKeyPath, item.__autocomplete_queryID);
    }

    // Populate href to enable keyboard navigation and also retain focus on click
    const fakeLinkHref = `#${serializeShopState(new NavigationState(StorefrontPage.Shop, "", [], encodedKeyPath))}`;

    return <a key={encodedKeyPath.join(";")} href={fakeLinkHref} onClick={onClick} className="list-item">
      {
        imageUrl ? <div
          className="thumbnail-image flex-shrink-0"
          data-lazy-images-target="image"
          data-lazy-images-imagery-path-value={imageUrl}
          data-lazy-images-imagery-variant="50x50"
        /> : <ImageSquareFallback className="thumbnail-image icon streamline-icon is-medium"/>
      }
      <span className="label-text description">{productVariant.title}</span>
    </a>
  }

  queryResults(searchClient: LiteClient, setContext: SetContext): AutocompleteSource<ProductResult> | boolean | undefined {
    return {
      sourceId: this.queryCollectionId,
      getItems: ({ query }: { query: string }) => {
        if (query.length == 0) return [];
        return getAlgoliaResults<ProductResult>({
          searchClient,
          queries: [
            {
              indexName: this.indexName,
              params: {
                query,
                hitsPerPage: this.numResults,
                analyticsTags: [`storeID:${this.storeId}`],
                filters: this.filterString(),
              },
            },
          ],
          transformResponse({ results, hits }) {
            if ("nbHits" in results[0] ) {
              setContext({
                nbProducts: results[0].nbHits
              });
            }

            return hits;
          }
        });
      },
      templates: {
        item() {
          return '<div></div>'
        }
      }
    };
  }

  // TODO: "See all results" full page search for products?
  searchResultsPath() {
    return undefined;
  }

  upperCaseTopSearch(ts: string): string {
    return ts.charAt(0).toUpperCase() + ts.slice(1);
  }

  topSearchResult(): React.JSX.Element[] {
    return this.topSearchFetchResult.map((ts: TopSearch) => {
      return <a href="#" onClick={event => this.handleTopSearchClick(event, ts.search)} key={ts.search} onMouseDown={event => event.preventDefault()} className="chip is-action"><SearchIcon className="icon streamline-icon" /><span>{this.upperCaseTopSearch(ts.search)}</span></a>
    })
  }

  handleTopSearchClick(event: React.MouseEvent, newQuery: string) {
    event.preventDefault();
    this.onTopSearchClick(newQuery);
  }

  topRecommendationResult() {
    return this.topRecommendations.map((productResult, index) => {
      return this.itemComponent(productResult, index);
    })
  }

  expandResults(event: React.MouseEvent, fullResultCount: number) {
    event.preventDefault();
    this.numResults = fullResultCount;
  }

  private selectMostRelevantVariant(result: ProductResult): ProductVariant {
    const variants = result.products;

    if (variants.length == 1) return variants[0];

    const now = Temporal.Now.plainDateTimeISO();
    const wday = now.dayOfWeek;
    const time = now.toPlainTime();

    // Temporal dayOfWeek goes from 1-7 (Sunday is 7)
    // Availability wday is 0-6 (Sunday is 0)
    const fulfillableVariantsForToday = variants.filter((product) => {
      return product.fulfillmentMethods.includes(this.selectedFulfillmentMethod);
    }).map((product) => {
      return {
        ...product,
        availabilities: product.availabilities.filter((availability) => availability.wday == (wday % 7)),
      }
    });

    return fulfillableVariantsForToday.sort(this.compareVariants(time))[0];
  }

  // A lower score means more likely to be displayed
  // The current preference on what variant to show is
  // 1. Show one that is currently fulfillable
  // 2. Show the one that is next orderable today
  // 3. Otherwise just show any variant
  variantScore(time: Temporal.PlainTime, variant: ProcessedProductVariant): number {
    if (variant.availabilities.length === 0) {
      return Number.POSITIVE_INFINITY; // If the variant doesn't have any availabilities for today, then it's not orderable
    } else if (variant.availabilities.some((a) => a.covers(time))) {
      // If variant is currently fulfillable, we want to show this one so give it lowest possible value
      // We're ignoring the possibility of a product being currently available across two variants and needing to pick the 'better' one to show here
      return Number.NEGATIVE_INFINITY;
    } else if (variant.availabilities.every((a) => { return a.entirelyBefore(time) })) {
      // If every product variant is no longer fulfillable, then let's not care about which one we show
      return Number.POSITIVE_INFINITY;
    } else {
      // Return the amount of time until the next fulfillable availability
      const nextUpcomingAvailabilities = variant.availabilities
        .filter((a) => { return time.until(a.startTime).sign >= 0 }) // Only availabilities that are upcoming
        .sort((availability1, availability2) => {
          return time.until(availability1.startTime).total("minutes") - time.until(availability2.startTime).total("minutes")
        });

      return time.until(nextUpcomingAvailabilities[0].startTime).total("minutes");
    }
  }

  compareVariants(now: Temporal.PlainTime) {
    return (variant1: ProductVariant, variant2: ProductVariant) => {
      const processedVariant1 = {
        ...variant1,
        availabilities: variant1.availabilities.map((availability) => new Availability(Temporal.PlainTime.from(availability.start_time), Temporal.PlainTime.from(availability.end_time), availability.wday)),
      }
      const processedVariant2 = {
        ...variant2,
        availabilities: variant2.availabilities.map((availability) => new Availability(Temporal.PlainTime.from(availability.start_time), Temporal.PlainTime.from(availability.end_time), availability.wday)),
      }

      return this.variantScore(now, processedVariant1) - this.variantScore(now, processedVariant2);
    }
  }
}
